import * as React from "react";
import { Badge } from "@/components/ui/badge";
import type { ContractFamilySummary } from "@/modules/snapshots/resultDetails/contractFamilySummary";

interface Props {
  summary: ContractFamilySummary;
}

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">{children}</h4>
);

export const ContractFamilySummaryDetails: React.FC<Props> = ({ summary }) => (
  <section aria-label="How this summary was consolidated" className="mt-4 rounded-md border border-gray-200 p-3">
    <div className="flex flex-wrap items-center gap-2">
      <h3 className="text-sm font-semibold text-gray-800">How this summary was consolidated</h3>
      {summary.consolidationApproach && <Badge variant="review-neutral">{summary.consolidationApproach}</Badge>}
    </div>
    {summary.consolidationActions && (
      <>
        <SectionHeading>Consolidation actions</SectionHeading>
        <p className="mt-1 text-sm text-gray-700">{summary.consolidationActions}</p>
      </>
    )}
    {summary.exclusionsOrUncertainties.length > 0 && (
      <>
        <SectionHeading>Exclusions and uncertainties</SectionHeading>
        <ul className="mt-1 space-y-1 rounded-md bg-[#fdf0db] p-2 text-sm text-[#a06b1a]">
          {summary.exclusionsOrUncertainties.map((item, index) => (
            <li key={index}>
              <span className="font-medium">{item.documentOrIssue}</span>
              {item.reason && <span>: {item.reason}</span>}
            </li>
          ))}
        </ul>
      </>
    )}
    {summary.documentsApplied.length > 0 && (
      <>
        <SectionHeading>Documents applied</SectionHeading>
        <ul className="mt-1 space-y-1 text-sm text-gray-700">
          {summary.documentsApplied.map((item, index) => (
            <li key={index}>
              <span className="font-medium">{item.documentName}</span>
              {item.effect && <span>: {item.effect}</span>}
            </li>
          ))}
        </ul>
      </>
    )}
  </section>
);
